import i18next from "i18next"
import { initReactI18next } from "react-i18next"
import { writeTimeZoneCookie } from "@/lib/time-zone-cookie"

export const LOCALES = ["en", "ru"] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = "en"
export const DEFAULT_NS = "common"

const LOCALE_KEY = "it2tv:locale"
const COOKIE_MAX_AGE = 31536000

export function isLocale(value: unknown): value is Locale {
  return value === "en" || value === "ru"
}

export function readLocaleCookie(): Locale | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|;\s*)locale=([^;]+)/)
  return isLocale(match?.[1]) ? (match![1] as Locale) : null
}

export function writeLocaleCookie(locale: Locale) {
  try {
    document.cookie = `locale=${locale};path=/;max-age=${COOKIE_MAX_AGE};samesite=lax`
  } catch {
    /* private mode */
  }
}

export function detectBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE
  return navigator.language?.toLowerCase().startsWith("ru") ? "ru" : DEFAULT_LOCALE
}

function readDocumentLocale(): Locale | null {
  if (typeof document === "undefined") return null
  return isLocale(document.documentElement.lang) ? document.documentElement.lang : null
}

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    /* SSR / private mode */
  }
  return readLocaleCookie() ?? readDocumentLocale() ?? detectBrowserLocale()
}

export function persistLocale(locale: Locale) {
  try {
    localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    /* SSR / private mode */
  }
  writeLocaleCookie(locale)
}

const jsonModules = import.meta.glob<{ default: Record<string, unknown> }>("./locales/*/*.json", { eager: true })

type Resources = Record<string, Record<string, Record<string, unknown>>>

const resources: Resources = {}
for (const [path, mod] of Object.entries(jsonModules)) {
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/)
  if (!match) continue
  const [, lng, ns] = match
  ;(resources[lng] ??= {})[ns] = mod.default
}

const namespaces = Object.keys(resources[DEFAULT_LOCALE] ?? {})
const initialLocale = readStoredLocale()

writeTimeZoneCookie(COOKIE_MAX_AGE)

void i18next.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: DEFAULT_NS,
  ns: namespaces,
  supportedLngs: LOCALES,
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
})

export function changeLocale(locale: Locale) {
  if (!isLocale(locale)) return
  persistLocale(locale)
  void i18next.changeLanguage(locale)
}

export default i18next
